import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AlertCircle, CheckCircle2, Circle, FileSearch, FileText, Gauge, Loader2, RefreshCw, XCircle,
  ChevronLeft, ChevronRight, Copy,
} from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { bulkReportService, type PipelineData } from '../services/bulkReportService';
import { InvoiceLink } from '../components/bills/InvoiceLink';
import { STAGE_ORDER, STAGE_LABELS, type StageState } from '../lib/billPipeline';
import { SEND_STATUS_LABELS } from '../lib/reportStatus';
import { runDurationLabel, type BillRunStatus } from '../lib/billRunLog';
import { buildBoardRows, type BoardRow } from '../lib/boardRows';
import { useBillVerdicts } from '../hooks/useBillVerdicts';
import { useBillFilters } from '../hooks/useBillFilters';
import { useWorkflowStep } from '../hooks/useWorkflowStep';
import { WORKFLOW_ORDER, WORKFLOW_LABELS, type BillWorkflowStatus } from '../lib/billWorkflow';
import { billFilterOptions, matchesBillFilters, type BillFilterContext } from '../lib/billFilters';
import { useRowSelection } from '../utils/useRowSelection';
import { BoardActionDialog, type BoardAction } from '../components/bills/BoardActionDialog';
import { DuplicatePeriodsDialog } from '../components/bills/DuplicatePeriodsDialog';
import { KeepThisOne } from '../components/bills/KeepThisOne';
import { WorkflowStepPicker } from '../components/bills/WorkflowStepPicker';
import { BulkStatusSelect } from '../components/bills/BulkStatusSelect';
import { BillFilterBar } from '../components/common/BillFilterBar';
import { MissingBillsByNmi } from '../components/billRuns/MissingBillsByNmi';

// Bulk Bill Run — every active bill and where it has got, from missing invoice
// to completed. The stages come from lib/billPipeline and the status from
// lib/billWorkflow (via lib/boardRows); this page loads, filters and draws them,
// and lets a person set the steps only a person can decide.

const PAGE_SIZE = 50;

function fmtDate(d: string | null | undefined) {
  return d ? new Date(d).toLocaleDateString('en-AU') : '—';
}

function fmtDateTime(d: string) {
  return new Date(d).toLocaleString('en-AU', {
    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

const RUN_STATUS_TEXT: Record<BillRunStatus, string> = {
  running: 'running now',
  done: 'completed',
  partial: 'completed with some failures',
  failed: 'failed',
  empty: 'had nothing to run',
};

// A board row as the shared filters read it (lib/billFilters).
const ctxOf = (r: BoardRow): BillFilterContext => ({
  verdict: r.verdict,
  workflow: r.status,
  sendStatus: r.sendStatus,
});

export function BulkBillRunPage() {
  const [data, setData] = useState<PipelineData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Search, months, site fields, verdict, status and send status: in the URL,
  // shared with Bills and Bulk Customer Report (hooks/useBillFilters).
  const { filters, query, setFilters, clearFilters, update, param } = useBillFilters();
  // `view=nmi` is the By NMI view (missing bills); its absence is By bill.
  const view: 'bill' | 'nmi' = param('view') === 'nmi' ? 'nmi' : 'bill';
  // The page lives in the URL (`page=`) so a bill opened from here can link
  // straight back to the same page of the same filtered board.
  const page = Math.max(1, Number.parseInt(param('page'), 10) || 1);
  const setPage = useCallback((n: number) => update({}, { page: n > 1 ? String(n) : '' }), [update]);
  const location = useLocation();
  const backState = { from: { label: 'Bulk Bill Run', to: `${location.pathname}${location.search}` } };
  const [action, setAction] = useState<BoardAction | null>(null);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const selection = useRowSelection();
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const { saving: savingStep, error: stepError, setStep } = useWorkflowStep(reload);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await bulkReportService.loadPipeline();
        if (!mounted) return;
        if (res.success && res.data) setData(res.data);
        else setError(res.error ?? 'Could not load bills');
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Could not load bills');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => { mounted = false; };
  }, [reloadKey]);

  // A filter change starts the board at page 1; the first render must not, or
  // a back link carrying `page=3` would land on page 1.
  const lastQuery = useRef(query);
  useEffect(() => {
    if (lastQuery.current === query) return;
    lastQuery.current = query;
    setPage(1);
  }, [query, setPage]);

  const boardBills = useMemo(() => data?.bills ?? [], [data]);
  const verdictFor = useBillVerdicts(boardBills);
  const rows = useMemo(() => (data ? buildBoardRows(data, verdictFor) : []), [data, verdictFor]);

  // Options come from the loaded bills, so each one matches at least one row.
  const options = useMemo(() => billFilterOptions(rows.map((r) => r.bill)), [rows]);
  // Every filter but Status narrows the board first: the status chips then
  // count what the other filters leave, so a chip's number is what it shows.
  const scopeRows = useMemo(
    () => rows.filter((r) => matchesBillFilters(r.bill, ctxOf(r), { ...filters, status: '' })),
    [rows, filters],
  );

  const statusCounts = useMemo(() => {
    const counts = Object.fromEntries(WORKFLOW_ORDER.map((s) => [s, 0])) as Record<BillWorkflowStatus, number>;
    for (const r of scopeRows) counts[r.status]++;
    return counts;
  }, [scopeRows]);
  const reportCount = scopeRows.filter((r) => r.pipeline.current === 'done').length;

  const filtered = useMemo(
    () => (filters.status ? scopeRows.filter((r) => r.status === filters.status) : scopeRows),
    [scopeRows, filters.status],
  );

  const lastPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, lastPage);
  const shown = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const lastRun = data?.lastRun ?? null;
  const filteredIds = filtered.map((r) => r.bill.id);
  // What is acted on is what is ticked and still on screen under the filters.
  const selectedRows = filtered.filter((r) => selection.isSelected(r.bill.id));
  // Bills sharing a period with another active bill (each period counted by all its bills).
  const duplicateBills = rows.filter((r) => r.twins.count > 0).length;

  return (
    <div data-testid="bill-runs-page" className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bulk Bill Run</h1>
          <p className="text-sm text-gray-500 mt-1">
            Every bill and how far it has got: bill period, parsing and NMI match, then the
            bill run (usage data, network charges, retail TOU, calculation), and the report.
          </p>
          <p data-testid="bill-runs-last-run" className="text-xs text-gray-500 mt-1.5">
            {lastRun
              ? <>Last bill run {fmtDateTime(lastRun.started_at)} — {RUN_STATUS_TEXT[lastRun.status]}
                  {lastRun.finished_at && ` in ${runDurationLabel(lastRun.started_at, lastRun.finished_at)}`}
                  {lastRun.status === 'failed' && lastRun.error && <span className="text-red-600"> · {lastRun.error}</span>}</>
              : 'No bill run recorded yet. Select bills and use Run bill run, or wait for the nightly run.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {view === 'bill' && duplicateBills > 0 && (
            <Button
              data-testid="bill-runs-fix-duplicates"
              variant="outline"
              size="sm"
              className="gap-1.5 border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => setDuplicatesOpen(true)}
              title="Bill periods with more than one active bill"
            >
              <Copy className="h-3.5 w-3.5" /> Fix duplicates ({duplicateBills} bills)
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)} disabled={loading} className="gap-1.5">
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
          </Button>
          <Button asChild size="sm" className="gap-1.5">
            <Link to={`/bill-validation/reports${query ? `?${query}` : ''}`} data-testid="bill-runs-open-reports">
              <FileText className="h-3.5 w-3.5" /> View reports
              {reportCount > 0 && <span className="ml-0.5">({reportCount})</span>}
            </Link>
          </Button>
        </div>
      </div>

      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
        {([['bill', 'By bill'], ['nmi', 'By NMI — missing bills']] as const).map(([v, label]) => (
          <button
            key={v}
            type="button"
            data-testid={`bill-runs-view-${v}`}
            data-active={view === v}
            onClick={() => update({}, { view: v === 'nmi' ? 'nmi' : '' })}
            className={cn(
              'px-3 py-1 rounded-md font-medium transition-colors',
              view === v ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'nmi' ? (
        <MissingBillsByNmi
          boardRows={rows}
          boardLoading={loading}
          boardError={error}
          reloadKey={reloadKey}
          filters={filters}
          onFilters={setFilters}
        />
      ) : (<>
      {/* Where bills are stuck: the filters every bill list shares, then the
          Status filter as one chip per status. */}
      <BillFilterBar
        filters={filters}
        onChange={setFilters}
        onClear={clearFilters}
        options={options}
        testIdPrefix="bill-runs"
        searchPlaceholder="Search NMI or client…"
      />
      <div className="flex flex-wrap items-center gap-2">
        <PhaseChip label={`All (${scopeRows.length})`} active={!filters.status} onClick={() => setFilters({ status: '' })} testId="all" />
        {WORKFLOW_ORDER.map((s) => (
          <PhaseChip
            key={s}
            testId={s}
            label={`At ${WORKFLOW_LABELS[s]} (${statusCounts[s]})`}
            active={filters.status === s}
            tone={s === 'completed' ? 'good' : statusCounts[s] > 0 ? 'attention' : 'none'}
            onClick={() => setFilters({ status: s })}
          />
        ))}
      </div>

      {selectedRows.length > 0 && (
        <div data-testid="bill-runs-selection-bar" className="flex items-center gap-2 flex-wrap rounded-lg border border-blue-200 bg-blue-50 px-4 py-2">
          <span className="text-sm font-medium text-blue-900">{selectedRows.length} selected</span>
          <Button variant="ghost" size="sm" onClick={selection.clear}>Clear</Button>
          <div className="ml-auto flex gap-2">
            <BulkStatusSelect
              testId="bill-runs-action-status"
              disabled={savingStep}
              onSet={(step) => void setStep(selectedRows.map((r) => r.bill.id), step)}
            />
            <Button data-testid="bill-runs-action-parse" variant="outline" size="sm" className="gap-1.5 bg-white" onClick={() => setAction('parse')}>
              <FileSearch className="h-3.5 w-3.5" /> Parse invoices
            </Button>
            <Button data-testid="bill-runs-action-run" size="sm" className="gap-1.5" onClick={() => setAction('run')}>
              <Gauge className="h-3.5 w-3.5" /> Run bill run
            </Button>
          </div>
        </div>
      )}

      {stepError && (
        <p data-testid="bill-runs-step-error" className="text-sm text-red-600">{stepError}</p>
      )}

      {loading && !data ? (
        <div data-testid="bill-runs-loading" className="text-center py-16 text-gray-400">Loading bills…</div>
      ) : error ? (
        <div data-testid="bill-runs-error" className="flex flex-col items-center gap-2 py-16 text-red-500">
          <AlertCircle className="h-5 w-5" />
          <p className="text-sm">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="bill-runs-empty" className="text-center py-16 text-gray-400">
          {rows.length === 0 ? 'No bills yet — bills appear here once an invoice is assigned to a site.' : 'No bills match the current filter.'}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="bill-runs-table" className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
                  <th className="pl-4 pr-1 py-2 w-8">
                    <input
                      data-testid="bill-runs-select-all"
                      type="checkbox"
                      aria-label="Select all bills matching the filter"
                      title="Select all bills matching the filter"
                      checked={selection.allSelected(filteredIds)}
                      ref={(el) => { if (el) el.indeterminate = !selection.allSelected(filteredIds) && selection.someSelected(filteredIds); }}
                      onChange={() => selection.toggleAll(filteredIds)}
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Client · NMI</th>
                  <th className="px-3 py-2 font-medium">Bill period</th>
                  {STAGE_ORDER.map((k) => (
                    <th key={k} className="px-1.5 py-2 font-medium text-center whitespace-nowrap" title={STAGE_LABELS[k]}>
                      {STAGE_LABELS[k]}
                    </th>
                  ))}
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shown.map(({ bill, pipeline, status, sendStatus, twins, bucket }) => {
                  const openStage = pipeline.stages.find((s) => s.key === pipeline.current);
                  return (
                    <tr key={bill.id} data-testid="bill-runs-row" data-bill-id={bill.id} data-status={status} className="align-top hover:bg-gray-50">
                      <td className="pl-4 pr-1 py-2.5">
                        <input
                          data-testid="bill-runs-row-select"
                          type="checkbox"
                          aria-label="Select bill"
                          checked={selection.isSelected(bill.id)}
                          onChange={() => selection.toggle(bill.id)}
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <Link
                          to={`/bill-validation/bills/${bill.id}`}
                          state={backState}
                          className="font-medium text-gray-900 hover:text-blue-600"
                        >
                          {bill.site?.client?.name ?? '—'}
                        </Link>
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-mono text-gray-500">{bill.site?.nmi ?? '—'}</p>
                          <InvoiceLink path={bill.invoice_path} />
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                        {bill.bill_from && bill.bill_to ? `${fmtDate(bill.bill_from)} – ${fmtDate(bill.bill_to)}` : '—'}
                      </td>
                      {pipeline.stages.map((s) => (
                        <td key={s.key} className="px-1.5 py-2.5 text-center">
                          <StageIcon state={s.state} title={`${STAGE_LABELS[s.key]}: ${s.detail ?? 'passed'}`} />
                        </td>
                      ))}
                      <td className="px-4 py-2.5 text-xs max-w-sm">
                        <p data-testid="bill-runs-status" className={cn(
                          'font-medium',
                          status === 'completed' ? 'text-emerald-700'
                            : status === 'mismatched' ? 'text-red-600' : 'text-gray-900',
                        )}>
                          {WORKFLOW_LABELS[status]}
                        </p>
                        {pipeline.current === 'done' ? (
                          <p className="text-gray-500">Report {SEND_STATUS_LABELS[sendStatus].toLowerCase()}</p>
                        ) : (
                          <p className={cn(
                            'break-words',
                            openStage?.state === 'fail' ? 'text-red-600'
                              : openStage?.state === 'running' ? 'text-blue-700' : 'text-gray-600',
                          )}>
                            <span className="text-gray-400">{STAGE_LABELS[pipeline.current]}: </span>
                            {pipeline.label}
                          </p>
                        )}
                        <WorkflowStepPicker
                          status={status}
                          bucket={bucket}
                          step={bill.workflow_status ?? null}
                          disabled={savingStep}
                          onChange={(step) => void setStep([bill.id], step)}
                        />
                        {twins.count > 0 && <KeepThisOne bill={bill} twins={twins} onDone={reload} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-2.5 border-t text-xs text-gray-500">
            <span data-testid="bill-runs-page-info">
              {filtered.length} bill{filtered.length === 1 ? '' : 's'} · page {safePage} of {lastPage}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <Button variant="outline" size="sm" disabled={safePage >= lastPage} onClick={() => setPage(safePage + 1)}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      </>)}

      {duplicatesOpen && data && (
        <DuplicatePeriodsDialog
          bills={data.bills}
          onClose={(changed) => { setDuplicatesOpen(false); if (changed) reload(); }}
        />
      )}

      {action && (
        <BoardActionDialog
          action={action}
          rows={selectedRows}
          onClose={(changed) => {
            setAction(null);
            if (changed) { selection.clear(); setReloadKey((k) => k + 1); }
          }}
        />
      )}
    </div>
  );
}

type PhaseChipProps = {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
  tone?: 'good' | 'attention' | 'none';
};

const PhaseChip = ({ label, active, onClick, testId, tone = 'none' }: PhaseChipProps) => (
  <button
    data-testid={`bill-runs-filter-${testId}`}
    data-active={active}
    onClick={onClick}
    className={cn(
      'px-3 py-1 rounded-full text-xs font-medium transition-colors border',
      active
        ? 'bg-blue-600 text-white border-blue-600'
        : tone === 'good' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
          : tone === 'attention' ? 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
            : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100',
    )}
  >
    {label}
  </button>
);

const StageIcon = ({ state, title }: { state: StageState; title: string }) => {
  const cls = 'h-4 w-4 inline-block';
  return (
    <span title={title} data-state={state} aria-label={title}>
      {state === 'pass' ? <CheckCircle2 className={cn(cls, 'text-emerald-600')} />
        : state === 'fail' ? <XCircle className={cn(cls, 'text-red-600')} />
          : state === 'running' ? <Loader2 className={cn(cls, 'text-blue-600 animate-spin')} />
            : <Circle className={cn(cls, 'text-gray-300')} />}
    </span>
  );
};

